import React, { useReducer, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { setNewPassword, forgotPasswordSubmit, forgotPassword } from '../../../utils/APIUtils';
import { USER } from '../../../constants/CommonConst';
import { handleValidation } from '../../../utils/formUtils';
import { notificationManager } from '../../../utils/NotificationUtils';
import '../login/login.scss';
import { Button, CircularProgress, InputAdornment, TextField } from '@material-ui/core';
import { VisibilityOff } from '@material-ui/icons';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { getUser } from '../../../utils/PropertyAPIUtils';
import { encryptLocalStorage, decryptLocalStorage } from '../../../utils/authUtils';
import { useDispatch } from 'react-redux';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../utils/loaderSlice';
import { getMFAStatus, setupMFA, verifyMFASetup, generateQRCodeURL } from '../../../utils/mfaUtils';
import { setAllBrands, setAllGroups, setAllHotels } from '../../property/propertySlice';
import QRCode from 'qrcode';

const SetNewPassword = (props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [fields, setFields] = useReducer((state, newState) => ({ ...state, ...newState }), {
    password: {
      value: '',
      fieldName: 'Password',
      errors: {},
      validations: ['required', 'validPassword'],
    },
    confirm_password: {
      value: '',
      fieldName: 'Confirm password',
      errors: {},
      validations: ['required', 'validConfirmPassword'],
    },
    otp: {
      value: '',
      fieldName: 'OTP',
      errors: {},
      validations: ['required'],
    },
  });

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setshowConfirmPassword] = useState(false);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [userNeedsMfaSetup, setUserNeedsMfaSetup] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [secretCode, setSecretCode] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);

  const validate = () => {
    let isValid = true;
    const updatedFields = {};
    for (let field in fields) {
      const currField = { ...fields }[field];
      if (handleValidation(currField)) isValid = false;

      if (currField.fieldName === 'OTP' && props.location.action !== 'forgotPassword')
        isValid = true;

      if (currField.fieldName === 'Confirm password') {
        if (fields.confirm_password.value !== fields.password.value) {
          isValid = false;
          currField.errors['validConfirmPassword'] = 'Invalid confirm password';
        } else {
          currField.errors['validConfirmPassword'] = null;
        }
      }

      updatedFields[field] = currField;
    }
    setFields(updatedFields);
    if (updatedFields.confirm_password.errors['validConfirmPassword']) {
      isValid = false;
    }
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setLoading(true);
      dispatch(stepUpLoaderCount());
      try {
        switch (props.location.action) {
          case 'newPassword':
            let user = props.location.user;
            if (user) {
              const response = await setNewPassword(user, fields.confirm_password.value);
              if (response) {
                const result = await getUser(user.username);
                const userData = result.data.getUserById;
                encryptLocalStorage(USER, userData);
                notificationManager('success', 'Password created successfully');
                
                // Check if user needs MFA setup
                if (userData.role !== 'SUPER_ADMIN' && userData.mfaEnforced === true) {
                  const currentMfaStatus = await getMFAStatus();
                  
                  if (!currentMfaStatus.enabled) {
                    setUserNeedsMfaSetup(true);
                    setShowMfaSetup(true);
                    setLoading(false);
                    return;
                  }
                }
                
                dispatch(setAllGroups([]));
                dispatch(setAllBrands([]));
                dispatch(setAllHotels([]));
                history.push('/');
              } else {
                localStorage.removeItem(USER);
                history.push('/login');
              }
            }
            break;

          case 'forgotPassword':
            const response = await forgotPasswordSubmit(
              props.location.email,
              fields.otp.value,
              fields.confirm_password.value,
            );
            if (response) {
              history.push('/login');
              notificationManager('success', 'Password created successfully');
            }
            break;

          default:
            history.push('/prelogin');
        }
      } catch (e) {
        notificationManager('danger', 'Failed to set new password');
        localStorage.removeItem(USER);
      } finally {
        setLoading(false);
        dispatch(stepDownLoaderCount());
      }
    }
  };

  const handleInputChange = (e, type) => {
    const inputValue = e.target.value;
    const currField = { ...fields }[type];

    currField.value = inputValue;
    handleValidation(currField);

    setFields({
      [type]: currField,
    });

    if (type === 'confirm_password' || type === 'password') {
      if (fields?.['confirm_password']?.value === fields?.['password']?.value) {
        fields['confirm_password'].errors['validConfirmPassword'] = null;
      } else {
        fields['confirm_password'].errors['validConfirmPassword'] = 'Invalid confirm password';
      }
    }

    let password = fields?.['password'];
    let email = history?.location?.email;

    if (email && password?.value?.toLowerCase()?.includes(email?.toLowerCase())) {
      fields['password'].errors['password'] = 'Password should not contain your email';
    }
  };

  const resendOTP = async () => {
    try {
      await forgotPassword(props.location.email);
    } catch (e) {
      notificationManager('danger', 'Failed to resend OTP');
    }
  };

  const handleSetupMFA = async () => {
    setSetupLoading(true);
    try {
      const user = decryptLocalStorage(USER);
      const secret = await setupMFA();
      setSecretCode(secret);

      const otpAuthUrl = generateQRCodeURL(secret, user.email);
      const qrDataUrl = await QRCode.toDataURL(otpAuthUrl);
      setQrCodeUrl(qrDataUrl);
    } catch (error) {
      notificationManager('danger', 'Failed to setup MFA: ' + error.message);
    } finally {
      setSetupLoading(false);
    }
  };

  const handleVerifyMFASetup = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      notificationManager('danger', 'Please enter a valid 6-digit code');
      return;
    }

    setSetupLoading(true);
    try {
      const success = await verifyMFASetup(verificationCode);
      if (success) {
        setShowMfaSetup(false);
        setUserNeedsMfaSetup(false);
        setVerificationCode('');
        setQrCodeUrl('');
        setSecretCode('');
        notificationManager('success', 'MFA setup completed successfully! Redirecting to dashboard...');
        
        dispatch(setAllGroups([]));
        dispatch(setAllBrands([]));
        dispatch(setAllHotels([]));
        history.push('/');
      }
    } catch (error) {
      notificationManager('danger', 'Error verifying MFA setup');
    } finally {
      setSetupLoading(false);
    }
  };

  useEffect(() => {
    if (showMfaSetup && userNeedsMfaSetup) {
      const handleBeforeUnload = (event) => {
        event.preventDefault();
        event.returnValue = '';
        return '';
      };

      const handlePopState = (event) => {
        window.history.pushState(null, '', window.location.href);
        notificationManager('info', 'Please complete MFA setup to continue.');
      };

      window.history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', handlePopState);
      window.addEventListener('beforeunload', handleBeforeUnload);

      const handleKeyDown = (event) => {
        if (
          (event.altKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) ||
          (event.key === 'Backspace' && event.target.tagName !== 'INPUT' && event.target.tagName !== 'TEXTAREA')
        ) {
          event.preventDefault();
          notificationManager('info', 'Please complete MFA setup to continue.');
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        window.removeEventListener('popstate', handlePopState);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [showMfaSetup, userNeedsMfaSetup]);

  if (showMfaSetup && userNeedsMfaSetup) {
    return (
      <div className='mfa-setup-scrollable-container scroll-styled'>
        <div className='mfa-setup-content'>
          <div className='row d-flex justify-content-center align-items-center mb-3 content mx-0'>
            <div className='header-row d-flex justify-content-center'>
              <span className='welcome-title'>Setup Multi-Factor Authentication</span>
            </div>
          </div>
          <div className='row d-flex justify-content-center align-items-center mb-3 mx-0'>
            <div className='col-md-10 text-center'>
              <p style={{ color: '#666', fontSize: '14px' }}>
                Your organization requires multi-factor authentication. Please set it up to continue.
              </p>
            </div>
          </div>

          {!qrCodeUrl && !secretCode ? (
            <div className='row d-flex justify-content-center align-items-center mt-4 mx-0'>
              <div className='col-md-12 mb-3 d-flex justify-content-center'>
                {setupLoading ? (
                  <CircularProgress color='primary' />
                ) : (
                  <Button variant='contained' color='primary' size='large' onClick={handleSetupMFA}>
                    SETUP MFA
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div>
              <div className='row d-flex justify-content-center align-items-center mb-2 mx-0'>
                <div className='col-md-8 text-center'>
                  <p style={{ color: '#666', fontSize: '12px', marginBottom: '10px' }}>
                    Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                  </p>
                  {qrCodeUrl && (
                    <div style={{ margin: '10px 0' }}>
                      <img src={qrCodeUrl} alt='MFA QR Code' style={{ maxWidth: '200px' }} />
                    </div>
                  )}
                  {secretCode && (
                    <div style={{ marginTop: '8px' }}>
                      <p style={{ color: '#666', fontSize: '10px', marginBottom: '4px' }}>Or enter this code manually:</p>
                      <div style={{ 
                        backgroundColor: '#f5f5f5', 
                        padding: '6px', 
                        borderRadius: '4px', 
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        wordBreak: 'break-all',
                        margin: '0 0 8px 0'
                      }}>
                        {secretCode}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className='row d-flex justify-content-center align-items-center mx-0'>
                <div className='col-md-5 mb-2 d-flex justify-content-center'>
                  <TextField
                    id='verificationCode'
                    label='Verification Code'
                    required
                    className='col-md-6'
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setVerificationCode(value);
                    }}
                    inputProps={{
                      maxLength: 6,
                      pattern: '[0-9]*',
                      inputMode: 'numeric',
                    }}
                    helperText='Enter the 6-digit code from your authenticator app'
                  />
                </div>
              </div>

              <div className='row d-flex justify-content-center align-items-center mt-2 mx-0'>
                <div className='col-md-12 mb-3 d-flex justify-content-center'>
                  {setupLoading ? (
                    <CircularProgress color='primary' />
                  ) : (
                    <Button 
                      variant='contained' 
                      color='primary' 
                      size='large' 
                      onClick={handleVerifyMFASetup}
                      disabled={!verificationCode || verificationCode.length !== 6}
                    >
                      VERIFY & COMPLETE SETUP
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className='row d-flex justify-content-center align-items-center mb-3 content mx-0'>
        <div className='header-row d-flex justify-content-center'>
          <span className='app-greeting ms-1'>
            {props.location.action === 'forgotPassword' ? 'Reset Password' : 'Create new password'}
          </span>
        </div>
      </div>

      <div className='row d-flex justify-content-center align-items-center mt-4  mx-0'>
        <div className='col-md-5 mb-3 d-flex justify-content-center'>
          <TextField
            id='password'
            label='New Password'
            required
            type={showPassword ? 'text' : 'password'}
            onChange={(e) => handleInputChange(e, 'password')}
            className='col-md-6'
            error={
              !!fields?.password?.errors?.required || !!fields?.password?.errors?.validPassword
            }
            helperText={
              fields?.password?.errors?.required || fields?.password?.errors?.validPassword
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  {!showPassword ? (
                    <VisibilityOff
                      style={{ height: '20px', width: '20px' }}
                      className='visible-icon'
                      onClick={() => setShowPassword(true)}
                    />
                  ) : (
                    <VisibilityIcon
                      style={{ height: '20px', width: '20px' }}
                      className='visible-icon'
                      onClick={() => setShowPassword(false)}
                    />
                  )}
                </InputAdornment>
              ),
            }}
          />
        </div>
      </div>
      <div className='row d-flex justify-content-center align-items-center mt-4  mx-0'>
        <div className='col-md-5 mb-3 d-flex justify-content-center'>
          <TextField
            id='confirm_password'
            label='Confirm Password'
            required
            type={showConfirmPassword ? 'text' : 'password'}
            onChange={(e) => handleInputChange(e, 'confirm_password')}
            className='col-md-6'
            error={
              !!fields?.confirm_password?.errors?.required ||
              !!fields?.confirm_password?.errors?.validPassword ||
              !!fields?.confirm_password?.errors?.validConfirmPassword
            }
            helperText={
              fields?.confirm_password?.errors?.required ||
              fields?.confirm_password?.errors?.validConfirmPassword
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  {!showConfirmPassword ? (
                    <VisibilityOff
                      style={{ height: '20px', width: '20px' }}
                      className='visible-icon'
                      onClick={() => setshowConfirmPassword(true)}
                    />
                  ) : (
                    <VisibilityIcon
                      style={{ height: '20px', width: '20px' }}
                      className='visible-icon'
                      onClick={() => setshowConfirmPassword(false)}
                    />
                  )}
                </InputAdornment>
              ),
            }}
          />
        </div>
      </div>
      {props.location.action === 'forgotPassword' && (
        <div className='row d-flex justify-content-center align-items-center mt-4  mx-0'>
          <div className='col-md-5 mb-3 d-flex justify-content-center'>
            <TextField
              id='otp'
              label='OTP'
              required
              onChange={(e) => handleInputChange(e, 'otp')}
              className='col-md-6'
              error={!!fields.otp.errors.required}
              helperText={fields.otp.errors.required}
            />
          </div>
        </div>
      )}
      <div className='row d-flex justify-content-center align-items-center mt-4 mx-0'>
        <div className='col-md-12 mb-3 d-flex justify-content-center'>
          {loading ? (
            <CircularProgress color='primary' />
          ) : (
            <Button variant='contained' type='submit' color='primary' size='large'>
              Submit
            </Button>
          )}
        </div>
      </div>
      {props.location.action === 'forgotPassword' && (
        <div className='col-md-12 mb-3 d-flex justify-content-center'>
          Didn’t receive OTP?&nbsp;
          <span className='forgot-password' onClick={resendOTP}>
            {' '}
            Resend
          </span>
        </div>
      )}
    </form>
  );
};

export default SetNewPassword;
